'use client';

import cx from 'classix';
import { useAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import CurrencySelect from '@/features/currency/components/currency-picker/currency-select/currency-select';
import { type CurrenciesList, type Currency } from '@/features/currency/types/currency-types';
import LanguageSelect from '@/features/language/components/language-picker/language-select/language-select';
import { type Language } from '@/features/language/types/language-types';
import { DEFAULT_LISTING_PART_VARIANTS, LISTING_URL_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import getUserListings from '@/features/listing/requests/get-user-listings';
import { type Listing, type ListingType } from '@/features/listing/types/listing-types';
import { type UserListingsData } from '@/features/listing/types/user-listings-types';
import getMegaphoneCount from '@/features/megaphone/requests/get-megaphone-count';
import { type MessagesCounters } from '@/features/messages/types/message-counters-types';
import SearchPlace from '@/features/place/components/search-place/search-place';
import { type CurrentPlace, type Place } from '@/features/place/types/place-types';
import placeNameToUrl from '@/features/place/utils/place-name-to-url';
import { isBannerClosedAtom } from '@/features/roomster-app/atoms/is-banner-closed-atom';
import AppBanner from '@/features/roomster-app/components/app-banner/app-banner';
import AppDownload from '@/features/roomster-app/components/app-download/app-download';
import { getCookieAppBannerHide } from '@/features/roomster-app/cookies/cookie-app-banner-hide';
import { helpdeskDataAtom } from '@/features/user/atoms/helpdesk-data-atom';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { type HelpdeskData } from '@/features/user/types/helpdesk-types';
import { type LoggedUser } from '@/features/user/types/user-types';
import { Link, useRouter } from '@/i18n/routing';
import HeaderLoginedLinks from '@/layout/header-logined-links/header-logined-links';
import Menu from '@/layout/menu/menu';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import postReportElastic from '@/shared/requests/post-report-elastic';
import Button from '@/ui/button/button';
import styles from './header.module.scss';
import useAppFlyerLink from "@/features/roomster-app/hooks/use-app-flyer-link";
interface HeaderProps {
  lastSearchedPlaces: Place[] | null;
  serviceType: ListingType | null;
  currentPlace: CurrentPlace | null;
  loggedUserData: LoggedUser | null;
  messagesCounters: MessagesCounters | null;
  helpdeskData: HelpdeskData | null;
  userListingsData: UserListingsData | null;
  languages: Language[];
  currencies: CurrenciesList;
  currentCurrency: Currency;
  listingsMissingPhoto: boolean;
  isNeedFetchUserListings: boolean;
  isHomePage?: boolean;
  isListingsListPage?: boolean;
  isFirstListing?: boolean;
  hideLoginButton?: boolean;
  userListingsPagination: {
    pageNumber: number;
    listingsPerPage: number;
  };
}
export default function Header(props: HeaderProps) {
  const {
    currencies,
    currentCurrency,
    currentPlace,
    helpdeskData,
    hideLoginButton,
    isFirstListing,
    isHomePage,
    isListingsListPage,
    isNeedFetchUserListings,
    languages,
    lastSearchedPlaces,
    listingsMissingPhoto,
    loggedUserData,
    messagesCounters,
    serviceType,
    userListingsData,
    userListingsPagination
  } = props;

  // const isMount = useIsMounted();

  // const [pageUrl,
  //   setPageUrl] = useState<string | null>(null);

  // let isHomePage: boolean | null = null;
  // let isListingsListPage: boolean | null = null;

  // if (pageUrl) {
  //   isHomePage = pageValidators.homePage(pageUrl);
  //   isListingsListPage = pageValidators.listingsListPage(pageUrl);
  // }

  useHydrateAtoms([[loggedUserAtom, loggedUserData]]);
  useHydrateAtoms([[helpdeskDataAtom, helpdeskData]]);
  const router = useRouter();
  const translationsUser = useTranslations('user');
  const translationsActions = useTranslations('global.actions');
  const isMounted = useIsMounted();
  const loginAppFlyerLink = useAppFlyerLink('mobile', 'mobileLoginButton', ROUTER_LINKS.phoneAuth);
  const userBearerCookie = getUserBearerCookie();
  const [loggedUser, setLoggedUser] = useAtom(loggedUserAtom);
  const [, setHelpdeskData] = useAtom(helpdeskDataAtom);
  const [isBannerClosed, setIsBannerClosed] = useAtom(isBannerClosedAtom);
  const listingTypeVariant = serviceType ? LISTING_URL_PART_VARIANTS[serviceType] : 'haveshare';
  const listingTypeSlug = DEFAULT_LISTING_PART_VARIANTS[listingTypeVariant];
  const placeFullName = currentPlace ? currentPlace.fullName : 'anywhere';
  const searchResultsLink = ROUTER_LINKS.searchResults(listingTypeSlug, placeNameToUrl(placeFullName));
  const isLoggedUserMissingPhoto = loggedUser?.missingFields.includes('Photo');
  const [megaphoneCount, setMegaphoneCount] = useState(0);
  const [listingMissingPhotoId, setListingMissingPhotoId] = useState(userListingsData?.listingMissingPhotoId);
  const [showListingsMissingPhoto, setShowListingsMissingPhoto] = useState(listingsMissingPhoto);
  const handleGoToCurrentPlace = () => {
    if (currentPlace) {
      router.push(searchResultsLink);
    }
  };
  const handleAddUserPhotoClick = () => {
    if (userBearerCookie) {
      postReportElastic({
        bearerToken: userBearerCookie,
        report: {
          action_name: 'user_photo_upload_button_clicked',
          details: {
            src: 'header'
          }
        },
        userAgent: globalThis.navigator.userAgent
      }).catch(() => null);
    }
    router.push(ROUTER_LINKS.accountProfile);
  };
  const handleAddListingPhotoClick = () => {
    if (userBearerCookie) {
      postReportElastic({
        bearerToken: userBearerCookie,
        report: {
          action_name: 'listing_photo_upload_button_clicked',
          details: {
            src: 'header'
          }
        },
        userAgent: globalThis.navigator.userAgent
      }).catch(() => null);
    }
    if (listingMissingPhotoId) {
      // TODO: make link as constant from ROUTER_LINKS
      router.push(`${ROUTER_LINKS.listingsEdit}/${String(listingMissingPhotoId)}`);
    } else {
      router.push(ROUTER_LINKS.accountListings);
    }
  };
  const checkListingsMissingPhoto = (bearerToken: string, pageNumber: number, pageSize: number, userId: number) => {
    getUserListings({
      bearerToken,
      listingTypes: ['haveapartment', 'haveshare'],
      pageNumber,
      pageSize,
      userId
    }).then(listingsData => {
      if (listingsData) {
        const hasNextPage = pageNumber * pageSize < listingsData.count;
        const nextPage = pageNumber + 1;
        if (listingsData.missingDataFields.includes('Photo')) {
          setShowListingsMissingPhoto(true);
          setListingMissingPhotoId(listingsData.listingMissingPhotoId);
        } else if (hasNextPage) {
          checkListingsMissingPhoto(bearerToken, nextPage, pageSize, userId);
        }
      }
    }).catch(() => null);
  };
  useLayoutEffect(() => {
    setIsBannerClosed(getCookieAppBannerHide());
  }, [isMounted, setIsBannerClosed]);
  useLayoutEffect(() => {
    setHelpdeskData(helpdeskData);
  }, [isMounted, helpdeskData, setHelpdeskData]);
  useLayoutEffect(() => {
    setLoggedUser(loggedUserData);
  }, [loggedUserData, isMounted, setLoggedUser]);
  useEffect(() => {
    if (userBearerCookie && loggedUserData) {
      (async () => {
        const pageNumber = 1;
        const listingsPerPage = 20;
        const maxMegaphoneCount = 20;
        let megaphoneCountResult = 0;
        const calculateMegaphoneCount = async (listings: Listing[]) => {
          const megaphonePromises = listings.map(listing => getMegaphoneCount({
            bearerToken: userBearerCookie,
            currencyName: 'USD',
            searchParams: `search_params.listing_id=${String(listing.id)}`
          }));
          const results = await Promise.all(megaphonePromises);
          results.forEach(result => {
            if (result.count > 0) {
              megaphoneCountResult += 1;
            }
          });
        };
        if (userListingsData?.listings && userListingsData.count > 0) {
          const getListingsForMegaphoneCounter = async (page: number) => {
            if (megaphoneCountResult > maxMegaphoneCount) {
              return;
            }
            const listingsForMegaphoneCounter = await getUserListings({
              bearerToken: userBearerCookie,
              pageNumber: page,
              pageSize: listingsPerPage,
              userId: loggedUserData.id
            });
            if (!listingsForMegaphoneCounter) {
              return;
            }
            await calculateMegaphoneCount(listingsForMegaphoneCounter.listings);
            if (listingsForMegaphoneCounter.count > listingsPerPage * page && megaphoneCountResult !== listingsPerPage) {
              await getListingsForMegaphoneCounter(page + 1);
            }
          };
          await calculateMegaphoneCount(userListingsData.listings);
          if (userListingsData.count > listingsPerPage && megaphoneCountResult <= maxMegaphoneCount) {
            await getListingsForMegaphoneCounter(pageNumber + 1);
          }
        }
        setMegaphoneCount(megaphoneCountResult);
      })().catch(() => null);
    }
  }, [isMounted, loggedUserData, userBearerCookie, userListingsData?.count, userListingsData?.listings]);

  // Get data for manage display add listing photo button
  useEffect(() => {
    const bearerTokenCookie = getUserBearerCookie();
    if (isNeedFetchUserListings && loggedUser && bearerTokenCookie) {
      checkListingsMissingPhoto(bearerTokenCookie, userListingsPagination.pageNumber, userListingsPagination.listingsPerPage, loggedUser.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNeedFetchUserListings, loggedUser, userListingsPagination.listingsPerPage, userListingsPagination.pageNumber]);
  const isPhotoMissing = showListingsMissingPhoto || isLoggedUserMissingPhoto;
  const scrollToTop = () => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0
    });
  };
  return <>
      <header className={cx(styles.header, isListingsListPage && styles.header__isListingsListPage)}>
        <div className={styles.top_banner}>
          <AppBanner isBannerClosed={isBannerClosed} setIsBannerClosed={setIsBannerClosed} />
        </div>

        <div className={styles.main}>
          {(loggedUser && isListingsListPage) ?? isHomePage ?? isFirstListing ? <button className={cx(styles.logo, (isFirstListing ?? isHomePage) && styles.logo__shiftRight, !isFirstListing && isPhotoMissing && styles.logo__noShift)} disabled={!isListingsListPage} onClick={scrollToTop}>
              <Image src="/images/roomster-logo/logo.svg" width={38} height={38} alt="roomster logo" priority />
            </button> : <Link aria-label="home link" className={cx(styles.logo, isPhotoMissing && styles.logo__noShift)} href={loggedUser ? searchResultsLink : ROUTER_LINKS.home}>
              <Image src="/images/roomster-logo/logo.svg" width={38} height={38} alt="roomster logo" priority />
            </Link>}

          {!isFirstListing && !isHomePage && <div className={cx(styles.search, isListingsListPage && styles.search__isListingsListPage, !isPhotoMissing && styles.search__rightSpace)}>
              <SearchPlace currentPlace={currentPlace} lastSearchedPlaces={lastSearchedPlaces} isNeedMoveForwardButton={!!currentPlace} moveForwardButtonCallback={handleGoToCurrentPlace} isOptionNavigateToPlace />
            </div>}

          {!isFirstListing && isLoggedUserMissingPhoto && <Button element="button" fontSize="s" className={styles.listingMissingPhoto} onClick={handleAddUserPhotoClick}>
              {}
              <span dangerouslySetInnerHTML={{
            __html: translationsUser.raw('missing-user-photo-two-lines')
          }} />
            </Button>}

          {!isFirstListing && !isLoggedUserMissingPhoto && showListingsMissingPhoto && <Button element="button" fontSize="s" className={styles.listingMissingPhoto} onClick={handleAddListingPhotoClick}>
              {}
              <span dangerouslySetInnerHTML={{
            __html: translationsUser.raw('missing-listing-photo-two-lines')
          }} />
            </Button>}

          {!isFirstListing && !isHomePage && <div className={styles.app_download}>
              <AppDownload />
            </div>}

          <div className={cx(styles.language_picker, loggedUser && styles.language_picker__logined)}>
            <LanguageSelect options={languages} />
          </div>

          <div className={cx(styles.currency_picker, loggedUser && styles.currency_picker__logined)}>
            <CurrencySelect currentCurrency={currentCurrency} currencies={currencies} />
          </div>

          {!isFirstListing && loggedUser && <div className={styles.header_loggedUserLinks}>
              <HeaderLoginedLinks megaphoneCount={megaphoneCount} messagesCounters={messagesCounters} />
            </div>}

          {!loggedUser && !hideLoginButton && <>
              <div className={styles.login}>
                <Button href={ROUTER_LINKS.phoneAuth} element="link" variant="outlined" size="lg" fullWidth>
                  {translationsActions('login')}
                </Button>
              </div>

              <div className={styles.login__mobile}>
                <Button href={loginAppFlyerLink} element="link" variant="outlined" size="s" fullWidth>
                  {translationsActions('login')}
                </Button>
              </div>
            </>}

          <div className={cx(styles.menu, loggedUser && styles.menu__logined)}>
            <Menu hideLoginButton={hideLoginButton} currencies={currencies} currentCurrency={currentCurrency} languages={languages} loggedUser={loggedUser} isFirstListing={isFirstListing} messagesCounters={messagesCounters} megaphoneCount={megaphoneCount} userListingsData={userListingsData} searchLink={searchResultsLink} />
          </div>
        </div>
      </header>

      <div className={cx(styles.header_margin, isListingsListPage && styles.header_margin__isListingsListPage, !isBannerClosed && styles.header_margin__showAppBanner)} />
    </>;
}